import { For, Show, type Component } from 'solid-js';
import type { StrategyRun } from '~/domain/simulation';
import { edgeVerdict } from '~/domain/simulation';

/**
 * What a run actually cost, and how much that figure can be trusted
 * (SPEC.md §9, SPEC-V2.md §9.1 and §9.5).
 *
 * The comparison table above this reports outcomes — how often ahead, how much
 * pocketed, how deep the drawdown went — and every one of those is a
 * *distribution*, shaped by the walk-away markers. Read alone they invite the
 * conclusion the app exists to refuse: that one arrangement of bets takes less
 * money than another. So the edge each run actually realised is stated here,
 * beside what the table's odds say it should be, with a plain sentence on
 * whether the gap is ordinary for the number of spins behind it.
 *
 * The standard error is a floor, not a confidence interval, and the wording
 * says so — see `edgeVerdict`.
 */

export interface EdgeHonestyProps {
  runs: readonly StrategyRun[];
  nameOf: (strategyId: string) => string;
  class?: string;
}

const EdgeHonesty: Component<EdgeHonestyProps> = (props) => (
  <Show when={props.runs.length > 0}>
    <section class={`rounded-lg border border-line bg-surface-1 p-3 ${props.class ?? ''}`}>
      <h3 class="mb-2 text-[10px] font-medium uppercase tracking-[0.12em] text-ink-faint">
        What the wheel took
      </h3>

      <div class="space-y-3">
        <For each={props.runs}>
          {(run) => {
            const verdict = () => edgeVerdict(run.stats);
            return (
              <div class="border-t border-line pt-2 first:border-t-0 first:pt-0">
                <div class="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                  <span class="text-[11px] text-ink">{props.nameOf(run.stats.strategyId)}</span>
                  <span class="flex items-baseline gap-3">
                    <span class="tnum text-[10px] text-ink-faint">
                      theoretical{' '}
                      <span class="text-ink-dim">
                        {(run.stats.theoreticalEdge * 100).toFixed(2)}%
                      </span>
                    </span>
                    <span class="tnum text-[15px] font-semibold text-loss">
                      {(run.stats.realisedEdge * 100).toFixed(2)}%
                    </span>
                  </span>
                </div>
                <p
                  class="mt-1 text-[10px] leading-relaxed"
                  classList={{
                    'text-ink-faint': verdict().withinNormal,
                    'text-remove': !verdict().withinNormal,
                  }}
                >
                  {verdict().statement}
                </p>
              </div>
            );
          }}
        </For>
      </div>

      <p class="mt-3 border-t border-line pt-2 text-[10px] leading-relaxed text-ink-faint">
        The figures above are distributions shaped by where you walk away. This is the
        edge, and no arrangement of spreads or choice of stake moves it.
      </p>
    </section>
  </Show>
);

export default EdgeHonesty;
